import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

/**
 * Contract-coverage fields mirrored down from the contact's company, plus the
 * engagement-staleness virtual filter.
 *
 * Same denormalization pattern as `contact-fields-deals.ts`: the value is a
 * real column on `public.contacts`, kept in step with `companies` by trigger,
 * so a contacts list can be filtered by "their company is under contract"
 * without a join.
 *
 * See `supabase/migrations/20260818160000_crm_derived_rollups.sql`.
 */
const CONTRACT_VIEWS = [
  "contacts-with-contract",
  "contacts-without-contract",
] as const;

export const contractContactFields = defineFieldConfigMap({
  // FIELD: has_signed_contract
  has_signed_contract: {
    type: "boolean",
    label: "Company Under Contract",
    mobileDetailSectionId: "company",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Maintained automatically from the contact's company. Reflects whether that company has at least one signed contract.",
    dataLineage: {
      kind: "denormalized",
      sourceTable: "companies",
      sourceField: "has_signed_contract",
      sourceForeignKeyField: "company_id",
      notes:
        "Mirrored from the company's trigger-populated has_signed_contract column. Updated when the contact's company_id changes or the company's contract state changes.",
    },
    filterType: "exact",
    width: 170,
    minWidth: 130,
    maxWidth: 210,
    icon: "FileSignature",
    groupable: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "has_signed_contract",
    savedViewMembership: CONTRACT_VIEWS,
  },
  // FIELD: contract_expires_at
  contract_expires_at: {
    type: "date",
    label: "Company Contract Expires",
    mobileDetailSectionId: "company",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Latest signed-contract expiry at the contact's company. Edit the contract, not the contact.",
    dataLineage: {
      kind: "denormalized",
      sourceTable: "companies",
      sourceField: "contract_expires_at",
      sourceForeignKeyField: "company_id",
      notes: "Mirrored from the company's trigger-populated contract_expires_at column.",
    },
    filterType: "dateRange",
    width: 180,
    minWidth: 140,
    maxWidth: 220,
    icon: "CalendarClock",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: ["thismonth", "nextmonth", "thisquarter", "nextquarter"],
      showCustomRange: true,
    },
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "contract_expires_at",
    savedViewMembership: ["contacts-with-contract"],
  },
  /**
   * FILTER: not_contacted_within_days
   * Virtual filter id (not a column). Saved views pass
   * `filter_not_contacted_within_days`; filter-conversion strips the `filter_`
   * prefix and routes here via `toGraphQLFilter`.
   *
   * The inverse of the `last_contacted_at` datetime presets, which all express
   * "contacted within X". Never-contacted rows count as cold.
   */
  not_contacted_within_days: {
    type: "number",
    label: "Not contacted in (days)",
    filterable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    formOnly: true,
    toGraphQLFilter: (value, context) => {
      const days = typeof value === "number" ? value : Number(value);
      // Returning an EMPTY object (not null) is deliberate: null falls through
      // to the generic converter, which would emit `{ <virtual id>: { eq: … } }`
      // for a field that is not a column and make PostgREST reject the whole
      // query. An empty object is truthy, so the generic path is skipped and
      // the filter contributes nothing.
      if (!Number.isFinite(days) || days < 0) return {};
      const cutoff = new Date(context.now);
      cutoff.setUTCDate(cutoff.getUTCDate() - days);
      return {
        or: [
          { last_contacted_at: { is: "NULL" } },
          { last_contacted_at: { lt: cutoff.toISOString() } },
        ],
      };
    },
  },
});
